import React, { useState, useEffect, useMemo, useCallback } from "react";
import WarningModal from "../modal";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCartDeliveryCharges,
  selectCartItems,
  selectCartRestaurantDetails,
  selectBranchDetails,
} from "../../redux/features/restaurants/restaurantSelectors";
import {
  convertToNumberKWD,
  getFormattedDate,
  toNumber,
} from "../../utils/common";
import { addToCart } from "../../api/post/addToCart";
import { transformAddToCartPayload } from "../../utils/menu";
import {
  clearCart,
  setCartSubTotal,
  setCartTotal,
  setCartItems,
  addVariantItem,
  removeVariantItem,
} from "../../redux/features/restaurants/restaurantSlice";
import {
  checkoutProcessAsync,
  pollOrderStatusAsync,
} from "../../redux/features/order/orderThunks";
import { 
  checkoutPayload,
  generateTimeSlots,
  isTimeSlotDisabled,
} from "../../utils/orders";
import {
  selectSearchedCustomer,
  selectSelectedAddress,
  selectServiceType,
} from "../../redux/features/customer/customerSelectors";
import {
  selectOrderInfo,
  selectOrderStatus,
  selectOrderType,
  selectOrderSchedule,
  selectOrderComments,
} from "../../redux/features/order/orderSelectors";
import {
  setOrderType,
  setOrderSchedule,
  setOrderComments,
  clearOrderInfo,
} from "../../redux/features/order/orderSlice";
import { emptyCartAsync } from "../../redux/features/restaurants/restaurantThunks";
import { setOrderStatus } from "../../redux/features/order/orderSlice";
import { getBranchDetails } from "../../api/get/getBranchDetails";

const OrderSummary = ({ buttonText = "Checkout" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const isCheckoutScreen = location.pathname === "/checkout";
  const isCompleteOrder = buttonText === "Complete Order";
  const isCheckout = buttonText === "Checkout";

  const cart = useSelector(selectCartItems);
  const orderInfo = useSelector(selectOrderInfo);
  const orderStatus = useSelector(selectOrderStatus);
  const orderType = useSelector(selectOrderType);
  const orderSchedule = useSelector(selectOrderSchedule);
  const savedComments = useSelector(selectOrderComments);
  const cartRestaurantDetails = useSelector(selectCartRestaurantDetails);
  const cartDeliveryCharges = useSelector(selectCartDeliveryCharges);
  const customerInfo = useSelector(selectSearchedCustomer);
  const selectedAddress = useSelector(selectSelectedAddress);
  const selectedBranchDetails = useSelector(selectBranchDetails);
  const serviceType = useSelector(selectServiceType);

  const [orderedItems, setOrderedItems] = useState(cart);
  const [tax, setTax] = useState(toNumber(0.0)); // 5% tax
  const [deliveryCharges, setDeliveryCharges] = useState(
    convertToNumberKWD(cartDeliveryCharges)
  );
  const [isModalOpen, setIsModalOpen] = useState(false); // State for modal visibility
  const [showPendingPayment, setShowPendingPayment] = useState(false); // State for "Pending Payment" button
  const [paymentStatus, setPaymentStatus] = useState(null); // State for payment button status
  const formattedDate = getFormattedDate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [comments, setComments] = useState(savedComments || "");

  // For date and time inputs - initialize with values from Redux if available
  const [selectedDate, setSelectedDate] = useState(() => {
    // Initialize from Redux on component mount
    if (orderSchedule && orderType === "schedule" && orderSchedule.date) {
      try {
        // Parse date in "Apr 29, 2025" format
        const dateStr = orderSchedule.date;
        const dateParts = dateStr.split(' ');
        const month = dateParts[0]; // "Apr"
        const day = dateParts[1].replace(',', ''); // "29" (remove comma)
        const year = dateParts[2]; // "2025"
        
        // Convert month name to month number
        const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const monthNumber = monthNames.findIndex(m => m === month) + 1;
        
        // Format as YYYY-MM-DD
        const formattedDate = `${year}-${String(monthNumber).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        console.log("INITIAL STATE - Date from Redux:", dateStr, "→", formattedDate);
        return formattedDate;
      } catch (error) {
        console.error("Error in initial state:", error);
      }
    }
    // Default to empty
    return "";
  });
  
  const [selectedTime, setSelectedTime] = useState(() => {
    // Initialize from Redux
    if (orderSchedule && orderType === "schedule" && orderSchedule.from_time) {
      if (orderSchedule.from_time && orderSchedule.to_time) {
        const timeValue = `${orderSchedule.from_time} - ${orderSchedule.to_time}`;
        console.log("INITIAL STATE - Time from Redux:", timeValue);
        return timeValue;
      } else {
        console.log("INITIAL STATE - Time from Redux (from_time only):", orderSchedule.from_time);
        return orderSchedule.from_time;
      }
    }
    // Default to empty
    return "";
  });

  // Initialize the date and time from orderSchedule if available
  useEffect(() => {
    if (orderSchedule && orderType === "schedule") {
      // If we have a saved schedule, parse the date and time
      if (orderSchedule.date && (orderSchedule.from_time || orderSchedule.to_time)) {
        try {
          // Parse date in "Apr 29, 2025" format
          const dateStr = orderSchedule.date; // e.g. "Apr 29, 2025"
          
          // Parse date parts manually to avoid browser inconsistencies
          const dateParts = dateStr.split(' ');
          const month = dateParts[0]; // "Apr"
          const day = dateParts[1].replace(',', ''); // "29" (remove comma)
          const year = dateParts[2]; // "2025"
          
          // Convert month name to month number
          const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
          const monthNumber = monthNames.findIndex(m => m === month) + 1;
          
          // Format as YYYY-MM-DD
          const formattedDate = `${year}-${String(monthNumber).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          
          console.log("REDUX SYNC - Date from Redux:", dateStr, "→", formattedDate);
          setSelectedDate(formattedDate);
          
          // Construct time slot value
          if (orderSchedule.from_time && orderSchedule.to_time) {
            const timeValue = `${orderSchedule.from_time} - ${orderSchedule.to_time}`;
            console.log("REDUX SYNC - Time from Redux:", timeValue);
            setSelectedTime(timeValue);
          } else if (orderSchedule.from_time) {
            // Handle case where only from_time exists
            console.log("REDUX SYNC - Time from Redux (from_time only):", orderSchedule.from_time);
            setSelectedTime(orderSchedule.from_time);
          }
        } catch (error) {
          console.error("Error parsing saved schedule date:", error);
        }
      }
    }
  }, [orderSchedule, orderType]);

  // Get today's date in YYYY-MM-DD format for date input min
  const today = useMemo(() => {
    // Force local timezone date calculation
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }, []);

  // Generate time slots based on branch hours and current time
  const timeSlots = useMemo(() => {
    if (
      !selectedBranchDetails ||
      !selectedBranchDetails.branch_hours ||
      !selectedDate
    ) {
      console.log("Missing data for time slots:", {
        hasBranchDetails: !!selectedBranchDetails,
        hasBranchHours: !!(selectedBranchDetails && selectedBranchDetails.branch_hours),
        selectedDate
      });
      return [];
    }
    
    // Log branch hours for debugging
    console.log("Branch hours data:", {
      branchHours: selectedBranchDetails.branch_hours,
      selectedDate,
      dayOfWeek: new Date(selectedDate + "T00:00:00").toLocaleDateString('en-US', { weekday: 'long' })
    });

    return generateTimeSlots(
      selectedBranchDetails.branch_hours,
      selectedDate,
      today
    );
  }, [selectedDate, selectedBranchDetails, today]);

  // Determine which slots should be disabled
  const isSlotDisabled = (slot) => {
    return isTimeSlotDisabled(slot, selectedDate, today);
  };

  // Helper function to get selected options string
  const getSelectedOptions = (selectedVariant) => {
    return selectedVariant
      .flatMap((variant) =>
        variant.options
          .filter((opt) => opt.option_quantity > 0)
          .map((opt) => `${opt.option_name} (${opt.option_quantity})`)
      )
      .join(", ");
  };

  const handleCommentChange = (event) => {
    setComments(event.target.value);
  };

  // Helper function to calculate variant item price including base price and options
  const calculateVariantItemPrice = (item, variantItem) => {
    // Get base price of the item
    const basePrice = convertToNumberKWD(item.price);

    // Calculate options price
    const optionsPrice = variantItem.selectedVariant.reduce(
      (total, variant) => {
        const optionsTotal = variant.options.reduce((optTotal, opt) => {
          return optTotal + opt.unit_price * opt.option_quantity;
        }, 0);
        return total + optionsTotal;
      },
      0
    );

    // Return total price (base price + options price)
    return basePrice + optionsPrice;
  };

  // Handle adding more of an item
  const handleAddItem = (item) => {
    // For non-variant items only
    if (!item.has_variants) {
      const existingItem = { ...item, quantity: item.quantity + 1 };
      dispatch(setCartItems(existingItem));
    }
  };

  // Handle removing an item
  const handleRemoveItem = (item) => {
    // For non-variant items only
    if (!item.has_variants && item.quantity > 0) {
      const updatedItem = { ...item, quantity: item.quantity - 1 };
      dispatch(setCartItems(updatedItem));
    }
  };

  // Handle adding more of a variant item
  const handleAddVariantItem = (item, variantItem) => {
    dispatch(
      addVariantItem({
        productId: item.id,
        variantId: variantItem.variantId,
      })
    );
  };

  // Handle removing a variant item
  const handleRemoveVariantItem = (item, variantItem) => {
    dispatch(
      removeVariantItem({
        productId: item.id,
        variantId: variantItem.variantId,
      })
    );
  };

  // Calculate subtotal including variants
  const subtotal = useMemo(() => {
    return orderedItems?.reduce((total, item) => {
      if (item?.has_variants) {
        // For variant items
        return (
          total +
          item.items?.reduce((itemTotal, variantItem) => {
            const totalPrice = calculateVariantItemPrice(item, variantItem);
            return itemTotal + totalPrice * variantItem.quantity;
          }, 0)
        );
      } else {
        // For non-variant items
        return total + convertToNumberKWD(item.price) * toNumber(item.quantity);
      }
    }, 0);
  }, [orderedItems]);

  const totalPayable = useMemo(() => {
    return subtotal + subtotal * tax + deliveryCharges;
  }, [subtotal, tax, deliveryCharges]);

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  // Function to check if the selected time is valid in the current time slots
  const isSelectedTimeValid = useMemo(() => {
    if (!selectedTime || !selectedDate || orderType !== "schedule") {
      return false;
    }
    
    // Check if the selected time exists in the available time slots
    const validTimeSlots = timeSlots.filter(slot => !isSlotDisabled(slot));
    return validTimeSlots.some(slot => slot.value === selectedTime);
  }, [selectedTime, selectedDate, timeSlots, orderType, isSlotDisabled]);

  // Validate the schedule before proceeding
  const validateSchedule = () => {
    if (orderType !== "schedule") {
      return true;
    }
    
    if (!selectedDate) {
      setError("Please select a date for your scheduled order");
      return false;
    }
    
    if (!selectedTime) {
      setError("Please select a time slot for your scheduled order");
      return false;
    }
    
    if (!isSelectedTimeValid) {
      setError("The selected time slot is no longer available. Please select another time.");
      setSelectedTime(""); // Reset the time selection since it's invalid
      return false;
    }
    
    return true;
  };

  const handleCheckoutProcess = async () => {
    try {
      const orderData = {
        customerId: customerInfo?.id || null,
        serviceType: serviceType || null, // Use Redux service type instead of customer's service_type
        addressId: selectedAddress?.id || null,
        total: totalPayable,
        subtotal: subtotal,
        deliveryCharges: deliveryCharges,
        restaurantId: selectedBranchDetails?.restaurant_id || null,
        branchId: selectedBranchDetails?.branch_id || null,
        comments: savedComments,
        orderType: orderType,
        schedule: orderSchedule,
      };
      const payload = checkoutPayload(orderData);

      const response = await dispatch(checkoutProcessAsync(payload));
      if (!response || !response.order_no) {
        throw new Error('Failed to process order');
      }
      return true;
    } catch (error) {
      console.error("Checkout process failed:", error);
      setError("Error processing order");
      return false;
    }
  };

  const handleProceed = async () => {
    try {
      // Revalidate before proceeding (in case time became invalid during modal being open)
      if (orderType === "schedule" && !validateSchedule()) {
        // Close modal and show error
        setIsModalOpen(false);
        return;
      }

      const customerId = customerInfo?.id;
      setIsLoading(true);
      setIsModalOpen(false);

      // Check if it's an ASAP order and the branch is closed
      if (orderType === "asap") {
        const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
        
        let branchHours;
        let todayHours;

        // For pickup orders, use the branch hours from selectedBranchDetails
        if (serviceType === "pickup") {
          branchHours = selectedBranchDetails?.branch_hours;
          todayHours = branchHours?.find(day => day.week_day === today);
        } 
        // For delivery orders, fetch fresh branch details from API
        else {
          const branchDetails = await getBranchDetails(
            selectedBranchDetails.restaurant_id,
            selectedBranchDetails.name_en
          );
          
          if (!branchDetails || !branchDetails.branch_hours) {
            setError("Unable to verify branch hours. Please try again.");
            setIsLoading(false);
            return;
          }

          todayHours = branchDetails.branch_hours.find(day => day.week_day === today);
        }
        
        console.log("Branch open status:", todayHours?.openingStatus);
        
        if (todayHours?.openingStatus !== "Open") {
          // Show warning modal for closed branch
          setClosedBranch(selectedBranchDetails);
          setShowClosedModal(true);
          setIsLoading(false);
          return;
        }
      }

      if (isCompleteOrder || isCheckoutScreen) {
        await dispatch(emptyCartAsync(customerId));
        // Regular checkout flow - add items to cart and navigate
        const apiCalls = orderedItems
          ?.map((item) => {
            if (item.has_variants) {
              return item.items?.map((variantItem) => {
                const payload = transformAddToCartPayload(
                  customerId,
                  item,
                  cartRestaurantDetails,
                  variantItem.instructions,
                  variantItem // Pass the specific variant item
                );
                return addToCart(payload);
              });
            } else {
              const payload = transformAddToCartPayload(
                customerId,
                item,
                cartRestaurantDetails,
                item.instructions
              );
              return addToCart(payload);
            }
          })
          .flat();
        
        try {
          // Wait for all API calls to complete
          await Promise.all(apiCalls);
          
          // Process checkout and only proceed if successful
          const success = await handleCheckoutProcess();
          
          if (success) {
            await dispatch(emptyCartAsync(customerId));
            dispatch(clearOrderInfo());
            setShowPendingPayment(true);
          }
        } catch (error) {
          console.error("Operation failed:", error);
          setError("Error processing order");
        }
      }
    } catch (error) {
      console.error("Operation failed:", error);
      setError("Error processing order");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle button click with stronger validation
  const handleButtonClick = () => {
    // Save comments to Redux
    dispatch(setOrderComments(comments));
    
    // Clear any previous errors
    setError(null);

    // Validate schedule selections
    if (!validateSchedule()) {
      return; // Stop if validation fails
    }
    
    if (isCompleteOrder || isCheckoutScreen) {
      // Show confirmation modal for complete order
      setIsModalOpen(true);
    } else {
      // For regular checkout, navigate directly to checkout
      navigate("/checkout");
    }
  };

  useEffect(() => {
    let intervalId;

    if (isCheckoutScreen && orderInfo?.order_no && showPendingPayment) {
      const { intervalId: id, promise } = dispatch(
        pollOrderStatusAsync(orderInfo.order_no)
      );
      intervalId = id;

      // Cleanup when component unmounts or dependencies change
      return () => {
        if (intervalId) {
          clearInterval(intervalId);
        }
      };
    } else {
      // If we're not on checkout screen, clear any existing interval
      if (intervalId) {
        clearInterval(intervalId);
      }
      // Also reset order status when leaving checkout
      if (!isCheckoutScreen) {
        dispatch(setOrderStatus(null));
      }
    }
  }, [showPendingPayment, orderInfo?.order_no, dispatch, isCheckoutScreen]);

  useEffect(() => {
    setDeliveryCharges(convertToNumberKWD(cartDeliveryCharges));
  }, [cartDeliveryCharges]);

  // Replace with memoization or derived state
  const paymentStatusLabel = useMemo(() => {
    const statuses = [
      { status: "Pending", label: "Pending Payment" },
      { status: "Failed", label: "Payment Failed" },
      { status: "Submitted", label: "Payment Successful" },
    ];
    return (
      statuses.find((s) => s.status === paymentStatus)?.label || paymentStatus
    );
  }, [paymentStatus]);

  useEffect(() => {
    setOrderedItems(cart);
  }, [cart]);

  useEffect(() => {
    dispatch(setCartTotal(totalPayable));
    dispatch(setCartSubTotal(subtotal));
  }, [totalPayable, subtotal, dispatch]);

  useEffect(() => {
    if (paymentStatus !== orderStatus) {
      setPaymentStatus(orderStatus);

      // Handle successful payment
      if (orderStatus === "Submitted") {
        // Clear the cart immediately
        dispatch(clearCart());
        dispatch(clearOrderInfo());

        // Navigate to search after a 2-second delay
        setTimeout(() => {
          navigate("/search");
        }, 2000);
      }
    }
  }, [orderStatus, dispatch, navigate]);

  // Handle order type change
  const handleOrderTypeChange = (type) => {
    dispatch(setOrderType(type));
    // If changing to ASAP, clear any schedule
    if (type === "asap") {
      dispatch(setOrderSchedule(null));
      setSelectedDate("");
      setSelectedTime("");
    }
  };

  // Update order schedule in Redux (memoized to prevent infinite loops)
  const updateOrderSchedule = useCallback(
    (date, time) => {
      if (date && time) {
        // Convert to required format for API (e.g., "9 Oct 2025")
        const dateObj = new Date(date + "T00:00:00");
        const day = dateObj.getDate();
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[dateObj.getMonth()];
        const year = dateObj.getFullYear();
        const formattedDate = `${day} ${month} ${year}`;

        // Parse time range
        const [startTime, endTime] = time.split(" - ");

        const scheduleObj = {
          date: formattedDate,
          from_time: startTime,
          to_time: endTime || null,
        };

        dispatch(setOrderSchedule(scheduleObj));
      }
    },
    [dispatch]
  );

  // Handle date change with strict validation
  const handleDateChange = (e) => {
    const selectedDateValue = e.target.value;

    // Create date objects for comparison (forcing time to be 00:00:00)
    const selectedDateObj = new Date(selectedDateValue + "T00:00:00");
    const todayObj = new Date(today + "T00:00:00");

    // Prevent selecting past dates
    if (selectedDateObj < todayObj) {
      // Reset to today if user somehow selects a past date
      setSelectedDate(today);
      return;
    }

    setSelectedDate(selectedDateValue);
    // Reset time when date changes
    setSelectedTime("");
  };

  // Handle time change
  const handleTimeChange = (e) => {
    setSelectedTime(e.target.value);
    updateOrderSchedule(selectedDate, e.target.value);
  };

  // Initial setup when component mounts - set default radio to ASAP if not already set
  useEffect(() => {
    if (!orderType) {
      dispatch(setOrderType("asap"));
    }
  }, [dispatch, orderType]);

  // Reset date/time when order type changes to schedule - WITH PRIORITY CHECK
  useEffect(() => {
    console.log("ORDER TYPE EFFECT - Type:", orderType, "Selected Date:", selectedDate);
    
    if (orderType === "schedule") {
      // Only reset to today's date if no date was previously selected
      // AND no date from Redux exists
      if (!selectedDate && (!orderSchedule || !orderSchedule.date)) {
        console.log("ORDER TYPE EFFECT - Setting default today:", today);
        setSelectedDate(today);
      } else {
        console.log("ORDER TYPE EFFECT - Keeping existing date:", selectedDate);
      }
    }
  }, [orderType, today, selectedDate, orderSchedule]);

  // Update Redux when date or time changes
  useEffect(() => {
    if (selectedDate && selectedTime && isSelectedTimeValid) {
      updateOrderSchedule(selectedDate, selectedTime);
    } else if (orderType === "schedule" && selectedDate && selectedTime && !isSelectedTimeValid) {
      // If we have a date and time selected, but it's not valid in the current time slots
      setError("The selected time slot is no longer available. Please select another time.");
      setSelectedTime(""); // Reset time selection
    }
  }, [selectedDate, selectedTime, updateOrderSchedule, isSelectedTimeValid, orderType]);

  // Auto-select first time slot when date changes or timeslots change
  useEffect(() => {
    // Don't run this effect if time is already selected or no date is selected
    if (!selectedDate || selectedTime || timeSlots.length === 0) return;

    // Only auto-populate if we have slots and we're in schedule mode
    if (orderType === "schedule" && timeSlots.length > 0) {
      const firstSlot = timeSlots[0]?.value || "";
      if (firstSlot) {
        setSelectedTime(firstSlot);
        // Note: We don't need to call updateOrderSchedule here as the above useEffect will handle it
      }
    }
  }, [timeSlots, selectedDate, selectedTime, orderType]);

  // Add this helper function to calculate dynamic height
  const getOrderedItemsHeight = (items) => {
    let totalItems = items?.reduce((count, item) => {
      if (item.has_variants) {
        return count + (item.items?.length || 0);
      }
      return count + 1;
    }, 0);

    // If 4 or fewer items, calculate height based on items
    // Each item is approximately 40px (24px height + 8px margin)
    if (totalItems <= 4) {
      return `${totalItems * 40}px`;
    }

    // For more than 4 items, set fixed height
    return "160px"; // 4 items * 40px
  };

  // Load comments from Redux when component mounts
  useEffect(() => {
    if (savedComments !== undefined) {
      setComments(savedComments);
    }
  }, [savedComments]);

  // Add state for closed branch modal
  const [showClosedModal, setShowClosedModal] = useState(false);
  const [closedBranch, setClosedBranch] = useState(null);

  const handleClosedModalClose = () => {
    setShowClosedModal(false);
    setClosedBranch(null);
  };

  return (
    <div className="fixed right-0 top-20 sm:w-[500px] h-screen bg-neutral-100">
      {/* Wrap all content in a scrollable container with padding */}
      <div className="h-full overflow-y-auto p-4 pb-40">
        {" "}
        {/* Increased padding bottom to ensure space for all content */}
        {/* Order Type Selection */}
        <div className="flex justify-between items-center mt-4 mb-2">
          <div className="flex items-center space-x-4 pl-2">
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="orderType"
                value="asap"
                checked={orderType === "asap"}
                onChange={() => handleOrderTypeChange("asap")}
                className="hidden"
              />
              <div
                className={`w-6 h-6 rounded-full border ${
                  orderType === "asap"
                    ? "border-black bg-white"
                    : "border-black"
                } flex items-center justify-center mr-2`}
              >
                {orderType === "asap" && (
                  <div className="w-4 h-4 rounded-full bg-[#38C188]"></div>
                )}
              </div>
              <span>ASAP</span>
            </label>

            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="orderType"
                value="schedule"
                checked={orderType === "schedule"}
                onChange={() => handleOrderTypeChange("schedule")}
                className="hidden"
              />
              <div
                className={`w-6 h-6 rounded-full border ${
                  orderType === "schedule"
                    ? "border-black bg-white"
                    : "border-black"
                } flex items-center justify-center mr-2`}
              >
                {orderType === "schedule" && (
                  <div className="w-4 h-4 rounded-full bg-[#38C188]"></div>
                )}
              </div>
              <span>Schedule Order</span>
            </label>
          </div>
        </div>
        {/* Schedule Date and Time Fields */}
        {orderType === "schedule" && (
          <div className="flex flex-wrap gap-2 mb-4">
            <input
              type="date"
              className={`border ${!selectedDate ? 'border-red-300' : 'border-amber-100'} rounded-xl px-3 py-2 w-full sm:w-[48%]`}
              value={selectedDate}
              onChange={handleDateChange}
              min={today}
              key={`date-input-${selectedDate}`} // Force re-render with key
            />

            <div className="w-full sm:w-[48%] relative">
              <select
                className={`border ${!selectedTime && selectedDate ? 'border-red-300' : 'border-amber-100'} rounded-xl px-3 py-2 w-full text-gray-500`}
                value={selectedTime}
                onChange={handleTimeChange}
                disabled={!selectedDate || timeSlots.length === 0}
                key={`time-select-${selectedTime}`} // Force re-render with key
              >
                <option value="">
                  {timeSlots.length === 0
                    ? "No time slots available"
                    : "Select Time"}
                </option>
                {timeSlots
                  .filter(slot => !isSlotDisabled(slot))
                  .map((slot, index) => (
                  <option key={index} value={slot.value}>
                    {slot.label}
                  </option>
                ))}
              </select>
              {!selectedTime && selectedDate && (
                <div className="text-red-500 text-xs mt-1 px-1">Please select a time slot</div>
              )}
            </div>
          </div>
        )}
        <div className="border border-amber-100 w-full max-w-md p-3 h-[440px] rounded-md m-auto mt-5 flex flex-col">
          <div className="flex flex-wrap justify-between gap-4">
            <h2 className="text-lg font-bold text-gray-50">
              {selectedBranchDetails?.restaurantName}
            </h2>
            <p className="text-xs text-gray-50 mt-2">{formattedDate}</p>
          </div>
          <p className="text-[10px] text-gray-50 -mt-1">
            {selectedBranchDetails?.name_en}
          </p>
          <h2 className="text-lg font-bold text-gray-50 mb-4 mt-5">
            Ordered Items
          </h2>

          {/* Dynamically sized order items section */}
          <div
            className="flex-1 overflow-y-auto pr-2"
            style={{ height: getOrderedItemsHeight(orderedItems) }}
          >
            <ul>
              {orderedItems?.map((item) => (
                <li key={item.id}>
                  {item.has_variants ? (
                    // Render variant items
                    item.items?.map((variantItem, index) => (
                      <div
                        key={variantItem.variantId}
                        className="mb-2 text-sm text-gray-50 font-semibold"
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex items-center">
                            {isCheckout && (
                              <div className="flex items-center mr-2">
                                <button
                                  onClick={() =>
                                    handleRemoveVariantItem(item, variantItem)
                                  }
                                  className="w-5 h-5 border border-gray-300 rounded-full flex items-center justify-center text-xs leading-none"
                                >
                                  <span
                                    className="inline-block"
                                    style={{ marginBottom: "2px" }}
                                  >
                                    -
                                  </span>
                                </button>
                                <span className="mx-1 text-xs">
                                  {variantItem.quantity}
                                </span>
                                <button
                                  onClick={() =>
                                    handleAddVariantItem(item, variantItem)
                                  }
                                  className="w-5 h-5 border border-gray-300 rounded-full flex items-center justify-center text-xs leading-none"
                                >
                                  <span
                                    className="inline-block"
                                    style={{
                                      marginLeft: "0.5px",
                                      marginBottom: "1px",
                                    }}
                                  >
                                    +
                                  </span>
                                </button>
                              </div>
                            )}
                            <span>{item.name}</span>
                          </div>
                          <span>
                            KWD{" "}
                            {(
                              calculateVariantItemPrice(item, variantItem) *
                              variantItem.quantity
                            ).toFixed(3)}
                          </span>
                        </div>
                        <div className="text-xs text-gray-400 ml-[62px]">
                          {getSelectedOptions(variantItem.selectedVariant)}
                        </div>
                        {variantItem.instructions && (
                          <div
                            className="text-xs text-gray-400 ml-[62px] italic truncate"
                            title={variantItem.instructions}
                          >
                            Note: {variantItem.instructions}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    // Render non-variant items
                    <div className="mb-2 text-sm text-gray-50 font-semibold flex justify-between items-center">
                      <div className="flex items-center">
                        {isCheckout && (
                          <div className="flex items-center mr-2">
                            <button
                              onClick={() => handleRemoveItem(item)}
                              className="w-5 h-5 border border-gray-300 rounded-full flex items-center justify-center text-xs leading-none"
                            >
                              <span
                                className="inline-block"
                                style={{ marginBottom: "2px" }}
                              >
                                -
                              </span>
                            </button>
                            <span className="mx-1 text-xs">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => handleAddItem(item)}
                              className="w-5 h-5 border border-gray-300 rounded-full flex items-center justify-center text-xs leading-none"
                            >
                              <span
                                className="inline-block"
                                style={{
                                  marginLeft: "0.5px",
                                  marginBottom: "1px",
                                }}
                              >
                                +
                              </span>
                            </button>
                          </div>
                        )}
                        <span>{item.name_en}</span>
                      </div>
                      <span>
                        KWD{" "}
                        {(
                          convertToNumberKWD(item.price) * item.quantity
                        ).toFixed(3)}
                      </span>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Fixed section for payment summary */}
          <div className="mt-auto">
            <img
              src="/Images/Menu/hr.png"
              className="w-full mb-4"
              alt="separator"
            />
            <h2 className="text-lg font-bold text-gray-50 mb-4">
              Payment Summary
            </h2>
            <ul>
              <li className="mb-2 flex justify-between">
                <span className="text-sm text-gray-50 font-semibold">
                  Subtotal
                </span>
                <span className="text-xs text-gray-50 font-semibold">
                  KWD {subtotal.toFixed(3)}
                </span>
              </li>
              <li className="mb-2 flex justify-between">
                <span className="text-sm text-gray-50 font-semibold">Tax</span>
                <span className="text-xs text-gray-50 font-semibold">
                  KWD {(subtotal * tax).toFixed(3)}
                </span>
              </li>
              <li className="mb-2 flex justify-between">
                <span className="text-sm text-gray-50 font-semibold">
                  Delivery Charges
                </span>
                <span className="text-xs text-gray-50 font-semibold">
                  KWD {deliveryCharges.toFixed(3)}
                </span>
              </li>
            </ul>
            <img
              src="/Images/Menu/hr.png"
              className="w-full my-3"
              alt="separator"
            />
            <h3 className="text-lg font-bold flex justify-between">
              <span className="text-sm text-gray-50 font-semibold">
                Total Payable
              </span>
              <span className="text-xs text-gray-50 font-semibold">
                KWD {totalPayable.toFixed(3)}
              </span>
            </h3>
          </div>
        </div>
        <div className="flex justify-center mt-2">
          <textarea
            className="border border-amber-100 w-full max-w-md h-[100px] p-3 rounded-md text-sm font-semibold text-gray-50"
            placeholder="Notes;"
            value={comments}
            onChange={handleCommentChange}
          ></textarea>
        </div>
        <div className="flex justify-center mt-2">
          <input
            type="text"
            className="border border-amber-100 w-full max-w-md h-11 p-3 rounded-md text-sm font-semibold text-gray-50"
            placeholder="Coupon Code"
          />
        </div>
        
        {/* Error message display */}
        {error && (
          <div className="flex justify-center mt-4">
            <div className="border border-red-500 bg-red-100 text-red-700 w-full max-w-md p-3 rounded-md">
              {error}
            </div>
          </div>
        )}
        
        {/* Payment status button - keep in scrollable area but ensure enough space to see it */}
        {showPendingPayment && isCheckoutScreen && (
          <div className="flex flex-col justify-center items-center mt-8 mb-16">
            <>
              <button
                className={`w-full sm:w-[222px] h-10 text-lg font-semibold p-2 rounded ${
                  paymentStatusLabel === "Pending Payment"
                    ? "bg-[#F7B501] text-white" // Styles for Pending Payment
                    : paymentStatusLabel === "Payment Failed"
                    ? "bg-[#F70101] text-white" // Styles for Payment Failed
                    : paymentStatusLabel === "Payment Successful"
                    ? "bg-[#00AF0D] text-white" // Styles for Payment Successful
                    : ""
                }`}
              >
                {paymentStatusLabel}
              </button>

              {/* Additional text below Pending Payment */}
              {paymentStatusLabel === "Pending Payment" && (
                <div className="text-center mt-2 px-4 max-w-xs">
                  <p className="text-sm text-gray-700">
                    Payment link has been sent to the customer. Waiting payment
                    to be completed.
                  </p>
                </div>
              )}
            </>
          </div>
        )}
        {/* Add closed branch warning modal */}
        {showClosedModal && closedBranch && (
          <WarningModal
            onCancel={handleClosedModalClose}
            onProceed={handleClosedModalClose}
            description={`${closedBranch.name_en} is currently closed for your area`}
            subDescription="Please try again during operating hours or schedule order for later"
            confirmButtonText="OK"
            cancelButtonText="Cancel"
            showItems={false}
          />
        )}
        {/* Fixed position buttons at bottom */}
        <div className="fixed bottom-0 right-0 sm:w-[500px] bg-neutral-100 p-4">
          <div className="flex flex-nowrap justify-between gap-4 px-2">
            <button
              onClick={() => navigate(-1)}
              className="w-full sm:w-[222px] h-10 text-amber-100 border border-amber-100 p-2 text-sm font-semibold rounded"
            >
              Back
            </button>
            <button
              onClick={handleButtonClick}
              disabled={
                isLoading ||
                (orderType === "schedule" && (!selectedDate || !selectedTime))
              }
              className={`w-full sm:w-[222px] h-10 text-lg font-semibold bg-amber-100 text-white p-2 rounded ${
                isLoading ||
                (orderType === "schedule" && (!selectedDate || !selectedTime))
                  ? "opacity-50"
                  : ""
              }`}
            >
              {isLoading ? (
                "Processing..."
              ) : showPendingPayment && isCheckoutScreen ? (
                <div className="flex items-center justify-around">
                  <span className="text-sm px-2">Resend Payment Link</span>
                  <img
                    src="/Images/Menu/resend.svg"
                    alt="resend"
                    className="ml-2"
                  />
                </div>
              ) : isCheckoutScreen ? (
                "Complete Order"
              ) : (
                buttonText
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Only show modal for Complete Order or on checkout screen */}
      {isModalOpen && (
        <WarningModal
          onCancel={handleCancel}
          onProceed={handleProceed}
          description={
            "Once you click proceed, customer will receive payment link to complete the order"
          }
          subDescription={"Proceed to complete order?"}
          confirmButtonText="Confirm"
          cancelButtonText="Cancel"
          showItems={false}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

export default OrderSummary;
